import React, { useState, useEffect } from "react";

import {
  Collapse,
  Descriptions,
  Spin,
  Table,
  Typography,
  Image,
  Space,
  Divider,
  Modal,
  Button,
  Form,
  Input,
  Radio,
  Affix,
} from "antd";

import { useParams, useNavigate } from "react-router";
import { Report } from "notiflix";

import "../styles.css";
import Footer from "../../../atoms/footer";
import Navbar from "../../../atoms/navbar";
import ImportantInfo from "../../../atoms/important-updates";

import { useIsAuthenticated } from "@azure/msal-react";
import { requestToken } from "../../../auth/AuthServices";
import HeaderFarms from "../../../utils/header_farms";
import API from "../../../services";
import moment from "moment";

const { Panel } = Collapse;
const { Link } = Typography;

// AttachmentViewer Component
const AttachmentViewer = ({ attachments = [] }) => {
  const [pdfPreviewSrc, setPdfPreviewSrc] = useState(null);

  const getMimeType = (content = "") => {
    if (content.startsWith("/9j/")) return "image/jpeg";
    if (content.startsWith("iVBOR")) return "image/png";
    if (content.startsWith("JVBER")) return "application/pdf";
    return "application/octet-stream";
  };

  const cleanedBase64 = (content = "") => content.replace(/\s/g, "");
  const openPdfModal = (src) => setPdfPreviewSrc(src);
  const closePdfModal = () => setPdfPreviewSrc(null);

  return (
    <div>
      <Space wrap size="middle">
        {attachments.map((file, idx) => {
          if (!file.content) return null;
          const base64 = cleanedBase64(file.content);
          const mimeType = getMimeType(base64);
          const isImage = mimeType.startsWith("image/");
          const isPdf = mimeType === "application/pdf";
          const dataUrl = `data:${mimeType};base64,${base64}`;

          return (
            <div
              key={file.RowIdent || idx}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div style={{ marginBottom: 15, textAlign: "center" }}>
                <i>{file.Calculated_AttachmentDesc}</i>
              </div>
              {isImage && (
                <Image
                  src={dataUrl}
                  alt={file.Calculated_AttachmentDesc}
                  width={200}
                  style={{ borderRadius: 10 }}
                />
              )}
              {isPdf && (
                <Button
                  type="primary"
                  size="middle"
                  onClick={() => openPdfModal(dataUrl)}
                >
                  Preview PDF
                </Button>
              )}
              {!isImage && !isPdf && (
                <Link
                  href={dataUrl}
                  download={file.Calculated_AttachmentDesc}
                  target="_blank"
                >
                  Download
                </Link>
              )}
            </div>
          );
        })}
      </Space>
      <Modal
        visible={!!pdfPreviewSrc}
        onCancel={closePdfModal}
        footer={null}
        width="80%"
        style={{ top: 20 }}
        bodyStyle={{ height: "80vh", padding: 0 }}
        title="PDF Preview"
      >
        {pdfPreviewSrc && (
          <iframe
            src={pdfPreviewSrc}
            title="PDF Preview"
            style={{ border: 0, width: "100%", height: "100%" }}
          />
        )}
      </Modal>

      <Divider />
    </div>
  );
};

const ViewEpicorPO = () => {
  const isAuthenticated = useIsAuthenticated();
  const { id } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [PO, setPO] = useState(null);

  // modal function and state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const showModal = () => {
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOk = () => {
    form.validateFields().then(async (values) => {
      setConfirmLoading(true);

      const accessToken = await requestToken();
      const epicorToken = localStorage.getItem("epicor_token");

      const headers = HeaderFarms(accessToken);
      const fullHeaders = { ...headers, token: epicorToken };

      try {
        if (values.approvalDecision === "approve") {
          await API.EpicorApprovePO(fullHeaders, id, {
            nextMessage: values.nextMessage,
          });
        } else {
          await API.EpicorRejectPO(fullHeaders, id, {
            nextMessage: values.nextMessage,
          });
        }

        Report.success(
          values.approvalDecision === "approve" ? "Approved" : "Rejected",
          `PO berhasil ${values.approvalDecision === "approve" ? "disetujui" : "ditolak"
          }`,
          "OK"
        );

        setIsModalVisible(false);

        return navigate("/epicor/pending");
      } catch (error) {
        console.warn("handleOk", error);
        if (error.response) {
          const status = error.response.status;
          let message = "An error occurred. Please try again..";

          if (status === 404) {
            message = "Data not found, maybe process in purchasing department.";
          } else if (status === 401) {
            message = "Password Invalid.";
          } else if (status === 429) {
            message = "Too many attempts. Please wait 15 minutes to try again.";
          } else {
            message = `Request failed ${status}.`;
          }
          form.resetFields(["password"]);

          setTimeout(() => {
            form.setFields([
              {
                name: "password",
                errors: [message],
              },
            ]);

            document.querySelector("input[name='password']")?.focus();
          }, 0);
        }
      } finally {
        setConfirmLoading(false);
      }
    });
  };

  const columnPODetail = [
    {
      title: "Line Number",
      dataIndex: "Calculated_LineNum",
      key: "Calculated_LineNum",
    },
    {
      title: "Part Number",
      dataIndex: "Calculated_PartNum",
      key: "Calculated_PartNum",
    },
    {
      title: "Description",
      dataIndex: "Calculated_Desc",
      key: "Calculated_Desc",
    },
    {
      title: "Qty",
      dataIndex: "Calculated_Qty",
      key: "Calculated_Qty",
      render: (val) => Number(val).toLocaleString("id-ID"),
    },
    {
      title: "Unit Cost",
      dataIndex: "Calculated_UnitCost",
      key: "Calculated_UnitCost",
      render: (val) =>
        `${Number(val).toLocaleString("id-ID")} ${PO?.Calculated_Currency || ""
        }`,
    },
    {
      title: "Total Cost",
      dataIndex: "Calculated_TotalCost",
      key: "Calculated_TotalCost",
      render: (val) =>
        `${Number(val).toLocaleString("id-ID")} ${PO?.Calculated_Currency || ""
        }`,
    },
  ];

  const fetchPO = async () => {
    const epicorToken = localStorage.getItem("epicor_token");

    if (!epicorToken) return navigate("/epicor/pending");

    try {
      const token = await requestToken();
      if (!token) return;
      const headers = HeaderFarms(token);
      const fullHeaders = {
        ...headers,
        token: epicorToken,
      };
      const data = await API.GetEpicorViewPO(fullHeaders, id);
      setPO(data);
    } catch (err) {
      if (
        err?.response?.status === 401 &&
        err?.response?.data?.error === "Invalid or expired token"
      ) {
        console.warn("Token expired atau tidak valid");
        localStorage.removeItem("epicor_token");
        navigate("/epicor/pending");
      } else {
        console.error(err);
        Report.warning(
          "Oops.. something wrong",
          "Sorry, this application is experiencing problems",
          "Okay"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) fetchPO();
  }, [isAuthenticated]);

  return (
    <>
      <Navbar
        title="View"
        back={true}
        subtitle="View PO"
        navigate="/epicor/pending"
      />
      <div className="container-fluid">
        <ImportantInfo />
        <div className="container mt-3 p-0" style={{ alignSelf: "center" }}>
          {loading && (
            <div className="fs-6 text-center mt-5" style={{ color: "#B7B7B7" }}>
              <Spin size="large" />
            </div>
          )}
          {PO && (
            <>
              <div className="row mt-1">
                <div className="col-md-12 mb-2">
                  <Collapse defaultActiveKey={["1"]}>
                    <Panel header={<div>Detail Information</div>} key="1">
                      <Descriptions
                        size="small"
                        bordered
                        title="Information"
                        className="mb-1"
                      >
                        <Descriptions.Item label="Purchase Order Number">
                          <b>{PO.Calculated_PONum}</b>
                        </Descriptions.Item>
                        <Descriptions.Item className="col-md-3 col-24" label="Supplier">
                          {PO.Calculated_SupplierName}
                        </Descriptions.Item>
                      </Descriptions>

                      <Descriptions size="small" bordered className="mb-4">
                        <Descriptions.Item
                          style={{ background: "#FF9900", color: "#FFFFFF" }}
                          label="Status"
                        >
                          Waiting Approval
                        </Descriptions.Item>
                        <Descriptions.Item className="col-md-3 col-24" label="Order Date">
                          {moment(PO.Calculated_OrderDate).format("LLL")}
                        </Descriptions.Item>
                      </Descriptions>

                      <Descriptions size="small" bordered className="mb-4">
                        <Descriptions.Item label="Total Cost" className="col-md-3 col-24">
                          {Number(PO.Calculated_Amount).toLocaleString("id-ID")}{" "}
                          {PO.Calculated_Currency}
                        </Descriptions.Item>
                      </Descriptions>

                      {PO.detail?.length > 0 && (
                        <>
                          <h6
                            className="mb-1"
                            style={{
                              background: "#d8d8d8",
                              padding: 10,
                              borderRadius: 2,
                            }}
                          >
                            Details Item
                          </h6>
                          <Table
                            dataSource={PO.detail}
                            rowKey="RowIdent"
                            className="custom-table"
                            pagination={{
                              hideOnSinglePage: true,
                              showLessItems: true,
                            }}
                            style={{ whiteSpace: "nowrap" }}
                            columns={columnPODetail}
                          />
                        </>
                      )}
                      {PO.attachment?.length > 0 && (
                        <>
                          <h6
                            className="mt-3"
                            style={{
                              background: "#d8d8d8",
                              padding: 10,
                              borderRadius: 2,
                            }}
                          >
                            Attachments
                          </h6>
                          <AttachmentViewer attachments={PO.attachment} />
                        </>
                      )}
                    </Panel>
                  </Collapse>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-12 text-end">
                  <Affix offsetBottom={30}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: 16,
                      }}
                    >
                      <Button
                        type="primary"
                        size="middle"
                        onClick={showModal}
                        style={{ padding: "0 10px" }}
                        disabled={loading}
                      >
                        Submit Decision
                      </Button>
                    </div>
                  </Affix>
                </div>
              </div>
            </>
          )}
          <Footer />
        </div>
      </div>
      <Modal
        title="Confirm Approval or Rejection"
        visible={isModalVisible}
        onOk={handleOk}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        okText="Submit"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            approvalDecision: null,
            password: "",
            nextMessage: "",
          }}
        >
          <Form.Item
            name="approvalDecision"
            label="Decision"
            rules={[{ required: true, message: "Select Approve or Reject" }]}
          >
            <Radio.Group>
              <Radio value="approve">Approve</Radio>
              <Radio value="reject">Reject</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="nextMessage"
            label="Note / Comment"
            rules={[
              { required: true, message: "Note is Mandatory" },
              { whitespace: true, message: "Note is Mandatory" },
            ]}
          >
            <Input.TextArea rows={4} maxLength={100} placeholder="Note" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ViewEpicorPO;
